import React, { useState } from "react";

import { addItem, updateItem, archiveItem } from "@/data/dataStore.js";

import { notifyDataUpdate } from "./useDataStore.js";

import { EntityStatusBadge } from "@/components/DocumentVisibilityBadge.jsx";

import { DataTable } from "@/components/DataTable.jsx";

import { useKarateUi } from "@/hooks/useKarateUi.js";



const EMPTY = {};



/**

 * @param {object} props

 * @param {'dojos'|'instructeurs'|'etudiants'} props.collectionKey

 * @param {object[]} props.rows

 * @param {{ key: string, label: string, render?: Function }[]} props.columns

 * @param {{ name: string, label: string, type?: string, required?: boolean, options?: {value:string,label:string}[] }[]} props.fields

 * @param {(values: object) => object} props.buildItem

 * @param {string} props.testId

 */

export function EntityCrudPanel({

  collectionKey,

  rows,

  columns,

  fields,

  buildItem,

  testId,

  searchFields = ["nom", "id"],

  backendCrud = null,

}) {

  const { ui } = useKarateUi();

  const [editingId, setEditingId] = useState(null);

  const [form, setForm] = useState(EMPTY);

  const [error, setError] = useState("");

  const [saving, setSaving] = useState(false);

  const useBackend = Boolean(backendCrud?.enabled);



  const startAdd = () => {

    setEditingId("__new__");

    setForm(Object.fromEntries(fields.map((f) => [f.name, ""])));

    setError("");

  };



  const startEdit = (row) => {

    setEditingId(row.id);

    setForm(

      Object.fromEntries(

        fields.map((f) => [f.name, row[f.name] == null ? "" : String(row[f.name])]),

      ),

    );

    setError("");

  };



  const cancel = () => {

    setEditingId(null);

    setForm(EMPTY);

    setError("");

  };



  const handleSubmit = async (e) => {

    e.preventDefault();

    try {

      for (const f of fields) {

        if (f.required && !String(form[f.name] ?? "").trim()) {

          throw new Error(ui.crud.requiredField(f.label));

        }

      }

      const item = buildItem(form, editingId === "__new__" ? null : editingId);

      setSaving(true);

      if (useBackend) {

        if (editingId === "__new__") {

          await backendCrud.onCreate(item);

        } else {

          await backendCrud.onUpdate(editingId, item);

        }

        await backendCrud.onRefresh?.();

      } else {

        if (editingId === "__new__") {

          addItem(collectionKey, item);

        } else {

          updateItem(collectionKey, editingId, item);

        }

        notifyDataUpdate();

      }

      cancel();

    } catch (err) {

      const msg = backendCrud?.formatError?.(err) || (err instanceof Error ? err.message : String(err));

      setError(msg);

    } finally {

      setSaving(false);

    }

  };



  const handleArchive = async (id) => {

    try {

      setSaving(true);

      if (useBackend) {

        await backendCrud.onArchive(id, rows.find((r) => r.id === id));

        await backendCrud.onRefresh?.();

      } else {

        archiveItem(collectionKey, id);

        notifyDataUpdate();

      }

    } catch (err) {

      setError(backendCrud?.formatError?.(err) || (err instanceof Error ? err.message : String(err)));

    } finally {

      setSaving(false);

    }

  };



  const tableColumns = [

    ...columns,

    {

      key: "_actions",

      label: ui.crud.actions,

      sortable: false,

      render: (row) => (

        <span className="row-actions">

          <button type="button" className="btn-link" onClick={() => startEdit(row)} data-testid={`${testId}-edit-${row.id}`}>

            {ui.crud.modify}

          </button>

          {row.statut !== "inactif" ? (

            <button type="button" className="btn-link" onClick={() => handleArchive(row.id)} data-testid={`${testId}-archive-${row.id}`}>

              {ui.crud.deactivate}

            </button>

          ) : null}

        </span>

      ),

    },

  ];



  return (

    <div data-testid={testId}>

      <div className="btn-row" style={{ marginBottom: "1rem" }}>

        <button type="button" className="btn-primary" onClick={startAdd} data-testid={`${testId}-add`} disabled={saving || backendCrud?.readOnly}>

          {ui.crud.add}

        </button>

      </div>

      {useBackend ? (

        <p className="backend-crud-notice" data-testid={`${testId}-backend-notice`}>

          {ui.backendCrud?.stagingWriteNotice}

        </p>

      ) : null}



      {editingId ? (

        <form className="crud-form" onSubmit={handleSubmit} data-testid={`${testId}-form`}>

          <h3>{editingId === "__new__" ? ui.crud.add : ui.crud.edit}</h3>

          {fields.map((f) => (

            <label key={f.name}>

              {f.label}

              {f.options ? (

                <select

                  value={form[f.name] ?? ""}

                  onChange={(e) => setForm((prev) => ({ ...prev, [f.name]: e.target.value }))}

                  data-testid={`${testId}-field-${f.name}`}

                >

                  <option value="">—</option>

                  {f.options.map((o) => (

                    <option key={o.value} value={o.value}>{o.label}</option>

                  ))}

                </select>

              ) : (

                <input

                  type={f.type || "text"}

                  value={form[f.name] ?? ""}

                  onChange={(e) => setForm((prev) => ({ ...prev, [f.name]: e.target.value }))}

                  data-testid={`${testId}-field-${f.name}`}

                />

              )}

            </label>

          ))}

          {error ? <p className="form-error" data-testid={`${testId}-error`}>{error}</p> : null}

          <div className="btn-row">

            <button type="submit" className="btn-primary" data-testid={`${testId}-save`} disabled={saving}>{ui.crud.save}</button>

            <button type="button" className="btn-secondary" onClick={cancel}>{ui.crud.cancel}</button>

          </div>

        </form>

      ) : null}



      <DataTable

        rows={rows}

        columns={tableColumns}

        searchFields={searchFields}

        filters={[

          {

            field: "statut",

            label: ui.columns.status,

            options: [

              { value: "actif", label: ui.status.actif },

              { value: "inactif", label: ui.status.inactif },

              { value: "a_confirmer", label: ui.status.a_confirmer },

            ],

          },

        ]}

        testId={`${testId}-table`}

      />

    </div>

  );

}



export function DemoFlag({ row }) {
  const { demoBadge, officialBadge } = useKarateUi();
  return row.demo ? (
    <span className="demo-badge" data-testid={`demo-${row.id}`}>{demoBadge}</span>
  ) : (
    <span className="official-badge">{officialBadge}</span>
  );
}

/** @deprecated use DemoFlag */
export function demoFlag(row) {
  return <DemoFlag row={row} />;
}



export { EntityStatusBadge };


